/**
 * @file useOrdersBoard.ts
 * @description Данные и перетаскивание доски заказов и возвратов (phase10.5;
 * phase19.31 — вынесено из `OrdersBoardPage`, страница стала раскладкой).
 *
 * Тип доски и воронка живут в адресе (`?type=orders|returns&pipelineId=`).
 * Воронки загружаются по типу; нет воронки в адресе — берём основную.
 * Перетаскивание карточки: сразу двигаем её в новую колонку, затем
 * `POST …/transition`; отказ сервера — откат и понятный текст.
 */

import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { useNotify } from "../components/ui/NotifyProvider";
import { readBoardType, type BoardType, type StageRow } from "../components/orders/board/board-types";
import { ApiError } from "../lib/api-client";
import {
  listOrderPipelines,
  listOrders,
  transitionOrderToStage,
  type OrderDto,
  type OrderPipelineWithStagesDto,
} from "../lib/api-orders";
import {
  listReturnPipelines,
  listReturns,
  transitionReturnToStage,
  type ReturnDto,
  type ReturnPipelineWithStagesDto,
} from "../lib/api-returns";

type Pipeline = OrderPipelineWithStagesDto | ReturnPipelineWithStagesDto;

/** Карточка, у которой есть стадия, — заказ или возврат. */
type StageCarrier = { id: string; stage: { id: string } | null };

/** Стадия в виде, в каком её отдаёт сервер у карточки (для мгновенного переноса). */
function stageOf(stage: StageRow) {
  const { id, code, name, kind, color, slaHours, sortOrder } = stage;
  return { id, code, name, kind, color, slaHours, sortOrder };
}

/** Понятный текст отказа: гвард «Собран» — подсказка, что делать. */
function dropErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.code === "picking_not_completed") {
    return "Заказ переходит в «Собран» только после проведения сборки.";
  }
  return err instanceof ApiError ? err.message : fallback;
}

export function useOrdersBoard(canManageOrders: boolean, canProcessReturns: boolean) {
  const notify = useNotify();
  const [params, setParams] = useSearchParams();
  const boardType = readBoardType(params.get("type"));
  const pipelineId = params.get("pipelineId") ?? "";

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [orders, setOrders] = useState<OrderDto[]>([]);
  const [returns, setReturns] = useState<ReturnDto[]>([]);
  const [pipesLoading, setPipesLoading] = useState(true);
  // Карточек ещё не было — первая загрузка; дальше перечитываем тихо.
  const [itemsLoading, setItemsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  /** Счётчик попыток: «Повторить» после ошибки перезапускает обе загрузки. */
  const [attempt, setAttempt] = useState(0);
  const before = useRef<{ orders: OrderDto[]; returns: ReturnDto[] }>({ orders: [], returns: [] });

  function select(type: BoardType, nextPipelineId?: string) {
    const next = new URLSearchParams({ type });
    if (nextPipelineId) next.set("pipelineId", nextPipelineId);
    setParams(next, { replace: true });
  }

  // Воронки по типу доски; нет нужной в адресе — основная.
  useEffect(() => {
    let cancelled = false;
    setPipesLoading(true);
    setError(null);
    const load: Promise<Pipeline[]> = boardType === "orders" ? listOrderPipelines() : listReturnPipelines();
    load
      .then((items) => {
        if (cancelled) return;
        setPipelines(items);
        setPipesLoading(false);
        if (items.length === 0) setItemsLoading(false);
        else if (!items.some((p) => p.id === pipelineId)) {
          const def = items.find((p) => p.isDefault) ?? items[0];
          if (def) select(boardType, def.id);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Не удалось загрузить воронки");
        setPipesLoading(false);
        setItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardType, attempt]);

  // Карточки выбранной воронки. Повторная загрузка — тихо, без пустой доски.
  useEffect(() => {
    if (!pipelineId) return;
    let cancelled = false;
    const load =
      boardType === "orders"
        ? listOrders({ limit: 200 }).then((res) => !cancelled && setOrders(res.items))
        : listReturns({ pipelineId, limit: 200 }).then((res) => !cancelled && setReturns(res.items));
    load
      .catch((err: unknown) => !cancelled && setError(err instanceof ApiError ? err.message : "Не удалось загрузить доску"))
      .finally(() => !cancelled && setItemsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [boardType, pipelineId, attempt]);

  const pipeline = pipelines.find((p) => p.id === pipelineId) ?? null;
  const stages: StageRow[] = (pipeline?.stages ?? []).filter((s) => s.isActive);

  function onDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  async function onDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const itemId = String(active.id);
    const overData = over.data.current as { type?: string; stageId?: string } | undefined;
    const isOrders = boardType === "orders";
    if (isOrders ? !canManageOrders : !canProcessReturns) return;

    const list: StageCarrier[] = isOrders ? orders : returns;
    const targetId = overData?.type === "stage" ? overData.stageId : list.find((x) => x.id === over.id)?.stage?.id;
    const item = list.find((x) => x.id === itemId);
    const target = stages.find((s) => s.id === targetId);
    if (!item || !target || item.stage?.id === target.id) return;

    before.current = { orders, returns };
    const move = <T extends StageCarrier>(rows: T[]): T[] =>
      rows.map((row) => (row.id === itemId ? { ...row, stage: stageOf(target) } : row));
    try {
      if (isOrders) {
        setOrders(move);
        const updated = await transitionOrderToStage(itemId, { toStageId: target.id });
        setOrders((prev) => prev.map((o) => (o.id === itemId ? updated : o)));
      } else {
        setReturns(move);
        const updated = await transitionReturnToStage(itemId, { toStageId: target.id });
        setReturns((prev) => prev.map((r) => (r.id === itemId ? updated : r)));
      }
    } catch (err) {
      setOrders(before.current.orders);
      setReturns(before.current.returns);
      notify.error(dropErrorText(err, isOrders ? "Не удалось перенести заказ" : "Не удалось перенести возврат"));
    }
  }

  return {
    boardType,
    pipelineId,
    pipelines,
    pipeline,
    stages,
    orders,
    returns,
    loading: pipesLoading || itemsLoading,
    error,
    activeOrder: boardType === "orders" ? (orders.find((o) => o.id === activeId) ?? null) : null,
    activeReturn: boardType === "returns" ? (returns.find((r) => r.id === activeId) ?? null) : null,
    selectType: (type: BoardType) => select(type),
    reload: () => setAttempt((n) => n + 1),
    selectPipeline: (id: string) => select(boardType, id),
    onDragStart,
    onDragEnd: (event: DragEndEvent) => void onDragEnd(event),
  };
}

export type OrdersBoardState = ReturnType<typeof useOrdersBoard>;
